import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

// Auto-refresh interval: every 4 hours (token lasts 24h, refresh well before expiry)
const REFRESH_INTERVAL_MS = 4 * 60 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const refreshTimerRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // Silent token refresh
  const silentRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return false;
    isRefreshingRef.current = true;
    try {
      await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
      isRefreshingRef.current = false;
      return true;
    } catch {
      isRefreshingRef.current = false;
      return false;
    }
  }, []);

  // Start periodic refresh timer
  const startRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = setInterval(() => {
      silentRefresh();
    }, REFRESH_INTERVAL_MS);
  }, [silentRefresh]);

  const stopRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Handle session expiration
  const handleSessionExpired = useCallback(() => {
    setSessionExpired(true);
    setUser(null);
    stopRefreshTimer();
  }, [stopRefreshTimer]);

  // Check auth on mount
  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(response.data);
      setSessionExpired(false);
      startRefreshTimer();
    } catch {
      // Try refresh
      const refreshed = await silentRefresh();
      if (refreshed) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
          setUser(response.data);
          setSessionExpired(false);
          startRefreshTimer();
        } catch {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [silentRefresh, startRefreshTimer]);

  useEffect(() => {
    checkAuth();
    return () => stopRefreshTimer();
  }, [checkAuth, stopRefreshTimer]);

  // Global Axios interceptor for 401 errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If 401 and not already retried and not an auth endpoint
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes("/auth/login") &&
          !originalRequest.url?.includes("/auth/refresh") &&
          !originalRequest.url?.includes("/auth/me")
        ) {
          originalRequest._retry = true;

          // Try silent refresh
          const refreshed = await silentRefresh();
          if (refreshed) {
            // Retry original request
            return axios(originalRequest);
          }

          // Refresh failed — session truly expired
          handleSessionExpired();
        }

        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [silentRefresh, handleSessionExpired]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(response.data);
      setSessionExpired(false);
      startRefreshTimer();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  };

  const logout = async () => {
    stopRefreshTimer();
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setSessionExpired(false);
  };

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuth,
    sessionExpired,
    clearSessionExpired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
